// #region Imports
import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import Individual from "marketplace/individuals/individual";
import MarketplaceTeamAPIService from "marketplace/teams/_api/marketplace-team-api-service";
import MarketplaceTeamSearch from "marketplace/teams/_api/request-contracts/marketplace-team-search";
import { TeamJSON } from "marketplace/teams/_schema/team-schema";
import { TeamInvitationJSON } from "marketplace/teams/invitations/_schemas/team-invitation/team-invitation-schema";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
import Session from "users/__session/session";
// #endregion

// #region Store State Type
export type MarketplaceTeamStoreState = {
  create: {
    saving: boolean;
    error: SerializedError | null;
  };
  vendors: {
    entries?: TeamJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  byId: {
    entries: Record<string, TeamJSON>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byQuery: {
    entries: Record<string, TeamJSON[]>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  avatars: {
    create: {
      saving: boolean;
      error: SerializedError | null;
    };
    entries: Record<string, string>;
    loading: Record<string, boolean>;
    saving: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  invitations: {
    create: {
      saving: boolean;
      error: SerializedError | null;
    };
    byTeamId: {
      entries: Record<string, TeamInvitationJSON[]>;
      loading: Record<string, boolean>;
      saving: Record<string, boolean>;
      error: Record<string, SerializedError | null>;
    };
    byId: {
      entries: Record<string, TeamInvitationJSON>;
      loading: Record<string, boolean>;
      saving: Record<string, boolean>;
      error: Record<string, SerializedError | null>;
    };
  };
};
export type MarketplaceTeamStoreStateType = {
  teams: MarketplaceTeamStoreState;
};

// #region Initial State
const initialState: MarketplaceTeamStoreState = {
  create: {
    saving: false,
    error: null,
  },
  vendors: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byId: {
    entries: {},
    loading: {},
    saving: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    saving: {},
    error: {},
  },
  avatars: {
    create: {
      saving: false,
      error: null,
    },
    entries: {},
    loading: {},
    saving: {},
    error: {},
  },
  invitations: {
    create: {
      saving: false,
      error: null,
    },
    byTeamId: {
      entries: {},
      loading: {},
      saving: {},
      error: {},
    },
    byId: {
      entries: {},
      loading: {},
      saving: {},
      error: {},
    },
  },
};

// #region Async Thunks
export const populateMarketplaceTeamVendors = createAsyncThunk<
  TeamJSON[],
  { session: Session; abortController?: AbortController }
>(
  "marketplaceTeams/getTeamVendors",
  async ({ session, abortController }: { session: Session; abortController?: AbortController }, thunkAPI) => {
    try {
      const apiService = new MarketplaceTeamAPIService({
        session,
        abortController,
      });
      return await apiService.getTeamVendors();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const populateMarketplaceTeamById = createAsyncThunk(
  "marketplaceTeams/getMarketplaceTeamById",
  async (
    {
      teamId,
      session,
      abortController,
    }: {
      teamId: Guid;
      session: Session;
      abortController?: AbortController;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({
        session,
        abortController,
      });
      return await apiService.getTeamById(teamId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const populateMarketplaceTeamsByIds = createAsyncThunk<Team[], { session: Session; ids?: Guid[] }>(
  "teams/getMarketplaceTeamsByIds",
  async ({ session, ids }: { session: Session; ids?: Guid[] }, thunkAPI) => {
    if (!ids?.length) return [];

    const store: MarketplaceTeamStoreStateType = thunkAPI.getState() as MarketplaceTeamStoreStateType;
    const missingTeamIds = _.uniqWith(
      ids.filter((id) => !store.teams.byId.entries[id.value]),
      (a, b) => a.isEqualTo(b),
    );
    if (!missingTeamIds.length) return [];

    const apiService = new MarketplaceTeamAPIService({ session });
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const result = await apiService.getMarketplaceTeamsByIds(missingTeamIds);
      if (!result.length) return [];
      return Team.fromJSONArray(result);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);
export const populateMarketplaceTeamsByQuery = createAsyncThunk(
  "marketplaceTeams/getMarketplaceTeamsByQuery",
  async (
    {
      query,
      session,
      abortController,
    }: {
      query: MarketplaceTeamSearch;
      session: Session;
      abortController?: AbortController;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session, abortController });
      return await apiService.searchTeams(query);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const populateMarketplaceTeamAvatar = createAsyncThunk(
  "marketplaceTeams/getMarketplaceTeamAvatar",
  async (
    {
      teamId,
      session,
      abortController,
    }: {
      teamId: Guid;
      session: Session;
      abortController?: AbortController;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({
        session,
        abortController,
      });
      return await apiService.getTeamAvatar(teamId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const populateMarketplaceTeamInvitations = createAsyncThunk(
  "marketplaceTeams/getMarketplaceTeamInvitations",
  async (
    {
      teamId,
      session,
      abortController,
    }: {
      teamId: Guid;
      session: Session;
      abortController?: AbortController;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({
        session,
        abortController,
      });
      return await apiService.getTeamInvitationsByTeamId(teamId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const populateMarketplaceTeamInvitationById = createAsyncThunk(
  "marketplaceTeams/getMarketplaceTeamInvitationById",
  async (
    {
      invitationId,
      session,
      abortController,
    }: {
      invitationId: Guid;
      session: Session;

      abortController?: AbortController;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({
        session,
        abortController,
      });
      return await apiService.getTeamInvitationByInvitationId(invitationId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const removeMarketplaceTeamProfile = createAsyncThunk(
  "marketplaceTeams/removeMarketplaceTeamProfile",
  async (
    {
      teamId,
      session,
    }: {
      teamId: Guid;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      await apiService.deleteTeam(teamId);
      return teamId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const removeMarketplaceTeamMember = createAsyncThunk(
  "marketplaceTeams/removeMarketplaceTeamMember",
  async (
    {
      teamId,
      memberId,
      session,
    }: {
      teamId: Guid;
      memberId: Guid;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      await apiService.removeTeamMember(teamId, memberId);
      return teamId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const createMarketplaceTeam = createAsyncThunk(
  "marketplaceTeams/createMarketplaceTeam",
  async (
    {
      team,
      session,
    }: {
      team: Team;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      return await apiService.createTeam(team);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const updateMarketplaceTeam = createAsyncThunk(
  "marketplaceTeams/updateMarketplaceTeam",
  async (
    {
      originalTeam,
      updatedTeam,
      session,
    }: {
      originalTeam: Team;
      updatedTeam: Team;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      return await apiService.updateTeam(originalTeam, updatedTeam);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const createMarketplaceTeamAvatar = createAsyncThunk(
  "marketplaceTeams/createMarketplaceTeamAvatar",
  async (
    {
      teamId,
      avatar,
      session,
    }: {
      teamId: Guid;
      avatar: File;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      return await apiService.createTeamAvatar(teamId, avatar);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const createMarketplaceTeamInvitation = createAsyncThunk(
  "marketplaceTeams/createMarketplaceTeamInvitation",
  async (
    {
      inviteeId,
      teamId,
      message,
      session,
    }: {
      inviteeId: Guid;
      teamId: Guid;
      message?: string;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      return await apiService.createTeamInvitation(inviteeId, teamId, message);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const cancelMarketplaceTeamInvitation = createAsyncThunk(
  "marketplaceTeams/cancelMarketplaceTeamInvitation",
  async (
    {
      invitationId,
      session,
    }: {
      invitationId: Guid;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      await apiService.cancelTeamInvitation(invitationId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const promoteMarketplaceTeamMemberToLeader = createAsyncThunk(
  "marketplaceTeams/promoteMarketplaceTeamMemberToLeader",
  async (
    {
      teamId,
      individual,
      session,
    }: {
      teamId: Guid;
      individual: Individual;
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      await apiService.promoteTeamMemberToLeader(teamId, individual);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
export const getMarketplaceTeamContactForum = createAsyncThunk(
  "companies/contactMarketplaceTeam",
  async ({ session, teamId }: { session: Session; teamId: Guid }, thunkAPI) => {
    try {
      const apiService = new MarketplaceTeamAPIService({ session });
      return await apiService.contactTeam(teamId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

// #region Slice
export const marketplaceTeamsSlice = createSlice({
  name: "marketplaceTeams",
  initialState,
  // #region Simple Reducers
  reducers: {
    addMarketplaceTeam: (state, action) => {
      const teamId = action.payload.id?.value;
      if (!teamId) return;
      state.byId.entries[teamId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((team) => team.id !== teamId) || [];
      state.vendors.entries.push(action.payload);
    },
    removeMarketplaceTeam: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
    addAvatar: (state, action) => {
      state.avatars.entries[action.payload.teamId.value] = action.payload.avatar;
    },
    removeAvatar: (state, action) => {
      delete state.avatars.entries[action.payload.value];
    },
    addMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.id?.value;
      if (!invitationId) return;
      state.invitations.byId.entries[invitationId] = action.payload;
    },
    removeMarketplaceTeamInvitation: (state, action) => {
      const invitationId = action.payload.value;
      if (!invitationId) return;
      delete state.invitations.byId.entries[invitationId];
      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }
    },
  },
  // #region Async Reducers
  extraReducers: (builder) => {
    // populateMarketplaceTeamVendors
    builder.addCase(populateMarketplaceTeamVendors.pending, (state, _action) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    });
    builder.addCase(populateMarketplaceTeamVendors.fulfilled, (state, action) => {
      state.vendors.entries = action.payload;
      state.vendors.loading = false;
    });
    builder.addCase(populateMarketplaceTeamVendors.rejected, (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.error;
    });

    // populateMarketplaceTeamById
    builder.addCase(populateMarketplaceTeamById.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.byId.loading[teamId] = true;
      state.byId.error[teamId] = null;
    });
    builder.addCase(populateMarketplaceTeamById.fulfilled, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.byId.loading[teamId] = false;
      if (!action.payload) {
        console.error("MarketplaceTeam not received by store.");
        return;
      }
      state.byId.entries[teamId] = action.payload;
    });
    builder.addCase(populateMarketplaceTeamById.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.byId.loading[teamId] = false;
      state.byId.error[teamId] = action.error;
    });

    // populateTeamsByIds
    builder.addCase(populateMarketplaceTeamsByIds.pending, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids?.length) return;
      for (const id of ids) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
      }
    });
    builder.addCase(populateMarketplaceTeamsByIds.fulfilled, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids?.length) return;
      for (const team of action.payload) {
        state.byId.entries[team.id.value] = team.toJSON();
      }
      for (const id of ids) {
        state.byId.loading[id.value] = false;
      }
    });
    builder.addCase(populateMarketplaceTeamsByIds.rejected, (state, action) => {
      const ids = action.meta.arg.ids;
      if (!ids?.length) return;
      for (const id of ids) {
        state.byId.loading[id.value] = false;
        state.byId.error[id.value] = action.error;
      }
    });

    // populateMarketplaceTeamsByQuery
    builder.addCase(populateMarketplaceTeamsByQuery.pending, (state, action) => {
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateMarketplaceTeamsByQuery.fulfilled, (state, action) => {
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.entries[query] = Object.assign([], action.payload.hits);
      state.byQuery.loading[query] = false;
    });
    builder.addCase(populateMarketplaceTeamsByQuery.rejected, (state, action) => {
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.error;
    });

    // populateMarketplaceTeamAvatar
    builder.addCase(populateMarketplaceTeamAvatar.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.avatars.loading[teamId] = true;
      state.avatars.error[teamId] = null;
    });
    builder.addCase(populateMarketplaceTeamAvatar.fulfilled, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.avatars.entries[teamId] = action.payload;
      state.avatars.loading[teamId] = false;
    });
    builder.addCase(populateMarketplaceTeamAvatar.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.avatars.loading[teamId] = false;
      state.avatars.error[teamId] = action.error;
    });

    // populateMarketplaceTeamInvitationById
    builder.addCase(populateMarketplaceTeamInvitationById.pending, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;
      state.invitations.byId.loading[invitationId] = true;
      state.invitations.byId.error[invitationId] = null;
    });
    builder.addCase(populateMarketplaceTeamInvitationById.fulfilled, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;
      state.invitations.byId.entries[invitationId] = action.payload;
      state.invitations.byId.loading[invitationId] = false;
    });
    builder.addCase(populateMarketplaceTeamInvitationById.rejected, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;
      state.invitations.byId.loading[invitationId] = false;
      state.invitations.byId.error[invitationId] = action.error;
    });

    // populateMarketplaceTeamInvitations
    builder.addCase(populateMarketplaceTeamInvitations.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.invitations.byTeamId.loading[teamId] = true;
      state.invitations.byTeamId.error[teamId] = null;
    });
    builder.addCase(populateMarketplaceTeamInvitations.fulfilled, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.invitations.byTeamId.entries[teamId] = action.payload;
      state.invitations.byTeamId.loading[teamId] = false;
    });
    builder.addCase(populateMarketplaceTeamInvitations.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      state.invitations.byTeamId.loading[teamId] = false;
      state.invitations.byTeamId.error[teamId] = action.error;
    });

    // removeMarketplaceTeamProfile
    builder.addCase(removeMarketplaceTeamProfile.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      // byId
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;

      // byQuery
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = true;
    });
    builder.addCase(removeMarketplaceTeamProfile.fulfilled, (state, action) => {
      const teamId = action.payload.value;

      // byId
      delete state.byId.entries[teamId];
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.entries = {};
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = false;

      // Remove from vendors array
      const targetIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (targetIndex !== -1 && state.vendors.entries) {
        state.vendors.entries.splice(targetIndex, 1);
      }
    });
    builder.addCase(removeMarketplaceTeamProfile.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      // byId
      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.error[teamId] = action.error;
      state.byQuery.saving[teamId] = false;
    });

    // removeMarketplaceTeamMember
    builder.addCase(removeMarketplaceTeamMember.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      // byId
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;

      // byQuery
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = true;
    });
    builder.addCase(removeMarketplaceTeamMember.fulfilled, (state, action) => {
      const teamId = action.payload.value;
      const team = state.byId.entries[teamId];

      if (team) {
        team.members = team.members.filter((member) => member.id !== action.meta.arg.memberId.value);
      }

      // byId
      state.byId.entries[teamId] = team;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.entries = {};
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = false;

      // vendors
      const vendorIndex = state.vendors.entries?.findIndex((t) => t.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1 && team) {
        state.vendors.entries[vendorIndex] = team;
      }
    });
    builder.addCase(removeMarketplaceTeamMember.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      // byId
      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.error[teamId] = action.error;
      state.byQuery.saving[teamId] = false;
    });

    // createMarketplaceTeam
    builder.addCase(createMarketplaceTeam.pending, (state, _action) => {
      state.create.error = null;
      state.create.saving = true;
    });
    builder.addCase(createMarketplaceTeam.fulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      state.create.error = null;
      state.create.saving = false;

      state.byId.entries[teamId] = action.payload;
      if (!state.vendors.entries) {
        state.vendors.entries = [];
      }
      state.vendors.entries.push(action.payload);
    });
    builder.addCase(createMarketplaceTeam.rejected, (state, action) => {
      state.create.error = action.error;
      state.create.saving = false;
    });

    // updateMarketplaceTeam
    builder.addCase(updateMarketplaceTeam.pending, (state, action) => {
      const updatedTeamJson = action.meta.arg.updatedTeam.toJSON();
      const teamId = updatedTeamJson.id;

      if (!teamId) return;

      // byId
      state.byId.entries[teamId] = updatedTeamJson;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;

      // byQuery
      state.byQuery.entries = {};
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = true;

      // vendors
      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = updatedTeamJson;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(updatedTeamJson);
      }
    });
    builder.addCase(updateMarketplaceTeam.fulfilled, (state, action) => {
      const teamId = action.payload.id;

      if (!teamId) return;

      // byId
      state.byId.entries[teamId] = action.payload;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.entries = {};
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = false;

      // vendors
      const vendorIndex = state.vendors.entries?.findIndex((team) => team.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1) {
        state.vendors.entries[vendorIndex] = action.payload;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(action.payload);
      }
    });
    builder.addCase(updateMarketplaceTeam.rejected, (state, action) => {
      const teamId = action.meta.arg.updatedTeam.id?.value;

      if (!teamId) return;

      // byId
      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.error[teamId] = action.error;
      state.byQuery.saving[teamId] = false;
    });

    // createMarketplaceTeamAvatar
    builder.addCase(createMarketplaceTeamAvatar.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      state.avatars.error[teamId] = null;
      state.avatars.saving[teamId] = true;
    });
    builder.addCase(createMarketplaceTeamAvatar.fulfilled, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      state.avatars.entries[teamId] = action.payload;
      state.avatars.error[teamId] = null;
      state.avatars.saving[teamId] = false;
    });
    builder.addCase(createMarketplaceTeamAvatar.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      state.avatars.error[teamId] = action.error;
      state.avatars.saving[teamId] = false;
    });

    // createMarketplaceTeamInvitation
    builder.addCase(createMarketplaceTeamInvitation.pending, (state, _action) => {
      state.invitations.create.error = null;
      state.invitations.create.saving = true;
    });
    builder.addCase(createMarketplaceTeamInvitation.fulfilled, (state, action) => {
      const teamId = action.payload.teamId;

      if (!state.invitations.byTeamId.entries[teamId]) {
        state.invitations.byTeamId.entries[teamId] = [];
      }

      state.invitations.byId.entries[action.payload.id] = action.payload;
      state.invitations.byTeamId.entries[teamId].push(action.payload);

      state.invitations.create.error = null;
      state.invitations.create.saving = false;
    });
    builder.addCase(createMarketplaceTeamInvitation.rejected, (state, action) => {
      state.invitations.create.error = action.error;
      state.invitations.create.saving = false;
    });

    // cancelMarketplaceTeamInvitation
    builder.addCase(cancelMarketplaceTeamInvitation.pending, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;

      state.invitations.byId.error[invitationId] = null;
      state.invitations.byId.saving[invitationId] = true;
    });
    builder.addCase(cancelMarketplaceTeamInvitation.fulfilled, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;

      // byTeamId
      for (const teamId in state.invitations.byTeamId.entries) {
        state.invitations.byTeamId.entries[teamId] = state.invitations.byTeamId.entries[teamId].filter(
          (invitation) => invitation.id !== invitationId,
        );
      }
      state.invitations.byTeamId.error[invitationId] = null;
      state.invitations.byTeamId.saving[invitationId] = false;

      // byId
      for (const id in state.invitations.byId.entries) {
        if (id === invitationId) {
          delete state.invitations.byId.entries[id];
        }
      }
      state.invitations.byId.error[invitationId] = null;
      state.invitations.byId.saving[invitationId] = false;
    });
    builder.addCase(cancelMarketplaceTeamInvitation.rejected, (state, action) => {
      const invitationId = action.meta.arg.invitationId.value;

      // byId
      state.invitations.byId.error[invitationId] = action.error;
      state.invitations.byId.saving[invitationId] = false;

      // byTeamId
      state.invitations.byTeamId.error[invitationId] = action.error;
      state.invitations.byTeamId.saving[invitationId] = false;
    });

    // promoteMarketplaceTeamMemberToLeader
    builder.addCase(promoteMarketplaceTeamMemberToLeader.pending, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = true;
    });
    builder.addCase(promoteMarketplaceTeamMemberToLeader.fulfilled, (state, action) => {
      const teamId = action.meta.arg.teamId.value;
      const newLeader = action.meta.arg.individual;
      const team = state.byId.entries[teamId];

      if (team) {
        team.leader = newLeader.toJSON();
      }

      // byId
      state.byId.entries[teamId] = team;
      state.byId.error[teamId] = null;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.entries = {};
      state.byQuery.error[teamId] = null;
      state.byQuery.saving[teamId] = false;

      // vendors
      const vendorIndex = state.vendors.entries?.findIndex((t) => t.id === teamId) ?? -1;
      if (state.vendors.entries && vendorIndex !== -1 && team) {
        state.vendors.entries[vendorIndex] = team;
      } else {
        state.vendors.entries = state.vendors.entries || [];
        state.vendors.entries.push(team);
      }
    });
    builder.addCase(promoteMarketplaceTeamMemberToLeader.rejected, (state, action) => {
      const teamId = action.meta.arg.teamId.value;

      // byId
      state.byId.error[teamId] = action.error;
      state.byId.saving[teamId] = false;

      // byQuery
      state.byQuery.error[teamId] = action.error;
      state.byQuery.saving[teamId] = false;
    });
  },
});

// #region Exports

// #region Team By Id

// getTeamById ----------------------------------------------------------------
const selectTeamById = createSelector(
  [
    (state: MarketplaceTeamStoreStateType) => state.teams.byId.entries,
    (_: MarketplaceTeamStoreStateType, id?: Guid) => id,
  ],
  (entries, id) => {
    if (!id) return undefined;
    const teamJson = entries[id.value];
    if (!teamJson) return undefined;
    return Team.fromJSON(teamJson);
  },
);
export const getTeamById = (id?: Guid) => (state: MarketplaceTeamStoreStateType) => selectTeamById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamById = (id?: Guid | null) => (state: RootState) => {
  if (!id) return false;
  return state.teams.byId.loading[id.value];
};

export const getErrorLoadingTeamById = (id?: Guid | null) => (state: RootState) => {
  if (!id) return;
  return state.teams.byId.error[id.value];
};

// #endregion

// #region Teams By Ids

// getTeamsByIds ----------------------------------------------------------
const selectTeamsByIds = createSelector(
  [
    (state: MarketplaceTeamStoreStateType) => state.teams.byId.entries,
    (_: MarketplaceTeamStoreStateType, ids: Guid[]) => ids,
  ],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is TeamJSON => !!x)
      .map((json) => Team.fromJSON(json)),
);
export const getTeamsByIds = (ids: Guid[]) => (state: MarketplaceTeamStoreStateType) => selectTeamsByIds(state, ids);
// ----------------------------------------------------------------------------

// getLoadingTeamsByIds ---------------------------------------------------
const selectTeamsLoadingByIds = createSelector(
  [(state: RootState) => state.teams.byId.loading],
  (loadingMap: Record<string, boolean>) => {
    return Object.keys(loadingMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getLoadingTeamsByIds = () => (state: RootState) => selectTeamsLoadingByIds(state);
// ----------------------------------------------------------------------------

// getErrorLoadingTeamsByIds ----------------------------------------------
const selectTeamErrorsByIds = createSelector(
  [(state: RootState) => state.teams.byId.error],
  (errorMap: Record<string, SerializedError | null>) => {
    return Object.keys(errorMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getErrorLoadingTeamsByIds = () => (state: RootState) => selectTeamErrorsByIds(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Team Vendors

// getTeamVendors ----------------------------------------------------------
const selectTeamVendors = createSelector([(state: RootState) => state.teams.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Team.fromJSON(json)).filter((c): c is Team => !!c),
);
export const getTeamVendors = () => (state: RootState) => selectTeamVendors(state);
// ----------------------------------------------------------------------------

export const getTeamVendorsIsLoading = () => (state: RootState) => state.teams.vendors.loading;

export const getTeamVendorsError = () => (state: RootState) => state.teams.vendors.error;

// #endregion

// #region Teams By Query

// getTeamsByQuery ------------------------------------------------------------
const selectTeamsByQuery = createSelector(
  [
    (state: MarketplaceTeamStoreStateType) => state.teams.byQuery.entries,
    (_: MarketplaceTeamStoreStateType, query: MarketplaceTeamSearch) => query,
  ],
  (entries, query) => Team.fromJSONArray(entries[query.cacheKey]),
);
export const getTeamsByQuery = (query: MarketplaceTeamSearch) => (state: RootState) => selectTeamsByQuery(state, query);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamsByQuery = (query: MarketplaceTeamSearch) => (state: RootState) =>
  state.teams.byQuery.loading[query.cacheKey];

export const getErrorLoadingTeamsByQuery = (query: MarketplaceTeamSearch) => (state: RootState) =>
  state.teams.byQuery.error[query.cacheKey];

// #endregion

// #region Team Avatar

export const getTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.entries[teamId.value] : undefined;

export const getIsLoadingTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.loading[teamId.value] : false;

export const getErrorLoadingTeamAvatar = (teamId: Guid | null) => (state: RootState) =>
  teamId ? state.teams.avatars.error[teamId.value] : undefined;

// #endregion

// #region Team Invitation by Id

// getTeamInvitationById ------------------------------------------------------
const selectTeamInvitationById = createSelector(
  [
    (state: MarketplaceTeamStoreStateType) => state.teams.invitations.byId.entries,
    (_: MarketplaceTeamStoreStateType, id: Guid) => id,
  ],
  (entries, id) => {
    const invitationJson = entries[id.value];
    if (!invitationJson) return undefined;
    return TeamInvitation.fromJSON(invitationJson);
  },
);
export const getTeamInvitationById = (id: Guid) => (state: RootState) => selectTeamInvitationById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamInvitationById = (id: Guid) => (state: RootState) =>
  state.teams.invitations.byId.loading[id.value];

export const getErrorLoadingTeamInvitationById = (id: Guid) => (state: RootState) =>
  state.teams.invitations.byId.error[id.value];

// #endregion

// #region Team Invitation by TeamId

// getTeamInvitationsByTeamId -------------------------------------------------
const selectTeamInvitationsByTeamId = createSelector(
  [
    (state: MarketplaceTeamStoreStateType) => state.teams.invitations.byTeamId.entries,
    (_: MarketplaceTeamStoreStateType, teamId: Guid) => teamId,
  ],
  (entries, teamId) => {
    const invitationsJson = entries[teamId.value];
    if (!invitationsJson) return undefined;
    return invitationsJson.map((json) => TeamInvitation.fromJSON(json));
  },
);
export const getTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? undefined : selectTeamInvitationsByTeamId(state, teamId);
// ----------------------------------------------------------------------------

export const getIsLoadingTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? false : state.teams.invitations.byTeamId.loading[teamId.value];

export const getErrorLoadingTeamInvitationsByTeamId = (teamId?: Guid) => (state: RootState) =>
  !teamId ? undefined : state.teams.invitations.byTeamId.error[teamId.value];
// #endregion
